import React, { useState } from 'react';
import { useStore, Product, getDispensaryStats, STASH_SELLER_THRESHOLD } from '../../store/useStore';
import { CheckCircle2, AlertCircle, TrendingUp, DollarSign, Package, X, ShieldCheck, MapPin } from 'lucide-react';
function FixModal({
  product,
  onClose,
  onResolve
}: {
  product: Product;
  onClose: () => void;
  onResolve: (action: 'out-of-stock' | 'wrong-item' | 'update-price', newPrice?: number) => void;
}) {
  const [reason, setReason] = useState<'out-of-stock' | 'wrong-item' | 'wrong-price' | null>(null);
  const [price, setPrice] = useState(product.price.toString());
  const handleSubmit = () => {
    if (reason === 'out-of-stock') {
      onResolve('out-of-stock');
    } else if (reason === 'wrong-item') {
      onResolve('wrong-item');
    } else if (reason === 'wrong-price') {
      onResolve('update-price', parseFloat(price) || product.price);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#252935]/40 backdrop-blur-sm" data-id="element-157">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-158">
        <div className="p-4 border-b border-[rgba(37,41,53,0.12)] flex items-center justify-between bg-[#F4F5F6]" data-id="element-159">
          <h2 className="font-bold text-[#252935]" data-id="element-160">Diagnose Issue</h2>
          <button onClick={onClose} className="p-1 text-[#60646F] hover:bg-gray-200 rounded-full" data-id="element-161">
            <X className="w-5 h-5" data-id="element-162" />
          </button>
        </div>

        <div className="p-6" data-id="element-163">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[rgba(37,41,53,0.12)]" data-id="element-164">
            <img src={product.image} alt={product.name} className="w-16 h-16 rounded-lg object-cover" data-id="element-165" />
            <div data-id="element-166">
              <h3 className="font-bold text-[#252935] text-lg leading-tight" data-id="element-167">
                {product.name}
              </h3>
              <p className="text-sm text-[#60646F]" data-id="element-168">{product.brand}</p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-169">
            <h4 className="font-bold text-[#252935] text-lg" data-id="element-170">
              Why did pickups fail for this item?
            </h4>
            <div className="space-y-3" data-id="element-171">
              <button onClick={() => setReason('out-of-stock')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${reason === 'out-of-stock' ? 'border-[#D93A4B] bg-[#D93A4B]/5' : 'border-[rgba(37,41,53,0.12)] hover:border-[#D93A4B]/50'}`} data-id="element-172">
                <div className="font-bold text-[#252935]" data-id="element-173">Not on shelf</div>
                <div className="text-sm text-[#60646F]" data-id="element-174">
                  We don't have this item in stock right now.
                </div>
              </button>

              <button onClick={() => setReason('wrong-item')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${reason === 'wrong-item' ? 'border-[#C98A2E] bg-[#FFF8ED]' : 'border-[rgba(37,41,53,0.12)] hover:border-[#C98A2E]/50'}`} data-id="element-175">
                <div className="font-bold text-[#252935]" data-id="element-176">Wrong item / SKU</div>
                <div className="text-sm text-[#60646F]" data-id="element-177">
                  The listing doesn't match what's actually on the shelf.
                </div>
              </button>

              <button onClick={() => setReason('wrong-price')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${reason === 'wrong-price' ? 'border-[#00CDBE] bg-[#E9FAF8]' : 'border-[rgba(37,41,53,0.12)] hover:border-[#00CDBE]/50'}`} data-id="element-178">
                <div className="font-bold text-[#252935]" data-id="element-179">Wrong price</div>
                <div className="text-sm text-[#60646F]" data-id="element-180">
                  The price on the shelf is different from the menu.
                </div>
              </button>
            </div>

            {reason === 'wrong-price' && <div className="mt-4 pt-4 border-t border-[rgba(37,41,53,0.12)]" data-id="element-181">
                <label className="block font-bold text-[#252935] mb-2" data-id="element-182">
                  Update the shelf price
                </label>
                <div className="relative" data-id="element-183">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#60646F] font-bold text-lg" data-id="element-184">
                    $
                  </span>
                  <input type="number" value={price} onChange={e => setPrice(e.target.value)} className="w-full border-2 border-[rgba(37,41,53,0.12)] rounded-xl py-3 pl-8 pr-4 text-lg font-bold text-[#252935] focus:outline-none focus:border-[#00CDBE]" data-id="element-185" />
                </div>
              </div>}
          </div>
        </div>

        <div className="p-4 border-t border-[rgba(37,41,53,0.12)] bg-[#F4F5F6] flex justify-end" data-id="element-186">
          <button onClick={handleSubmit} disabled={!reason} className={`px-6 py-2.5 rounded-full font-bold transition-colors ${reason ? 'bg-[#00CDBE] text-white hover:bg-[#00B8AA]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-187">
            {reason === 'out-of-stock' ? 'Mark out of stock' : reason === 'wrong-item' ? 'Pull from shelf' : reason === 'wrong-price' ? 'Update price' : 'Select a reason'}
          </button>
        </div>
      </div>
    </div>;
}
export function Overview() {
  const {
    products,
    dispensaries,
    currentDispensaryName,
    toggleProductStock,
    setBusinessView,
    posConnected,
    verifyProduct,
    resolveWrongItem
  } = useStore();
  const [showPosBanner, setShowPosBanner] = useState(!posConnected);
  const [fixingProductId, setFixingProductId] = useState<string | null>(null);
  const flaggedProducts = products.filter(p => p.status === 'Not verified' || p.status === 'At risk');
  const stashedCount = products.filter(p => p.status === 'Stashed').length;
  const fixingProduct = products.find(p => p.id === fixingProductId);
  const dispensaryStats = getDispensaryStats(products, dispensaries);
  const currentStats = dispensaryStats.find(d => d.name === currentDispensaryName);
  const isSeller = currentStats?.isStashSeller ?? false;
  const progress = Math.min((currentStats?.stashedSkus ?? 0) / STASH_SELLER_THRESHOLD, 1);
  return <div className="max-w-5xl mx-auto space-y-8 relative" data-id="element-188">
      {fixingProduct && <FixModal product={fixingProduct} onClose={() => setFixingProductId(null)} onResolve={(action, newPrice) => {
      if (action === 'out-of-stock') {
        verifyProduct(fixingProduct.id, false, fixingProduct.price);
      } else if (action === 'wrong-item') {
        resolveWrongItem(fixingProduct.id);
      } else if (action === 'update-price') {
        verifyProduct(fixingProduct.id, true, newPrice || fixingProduct.price);
      }
      setFixingProductId(null);
    }} data-id="element-189" />}

      <div className="flex justify-between items-end" data-id="element-190">
        <div data-id="element-191">
          <h1 className="text-3xl font-bold text-[#252935] mb-2" data-id="element-192">Overview</h1>
          <p className="text-[#60646F]" data-id="element-193">
            Here's how your menu is performing today.
          </p>
        </div>
        <button className="text-sm font-medium text-[#007E7A] hover:underline" data-id="element-194">
          How the badge works
        </button>
      </div>

      {showPosBanner && !posConnected && <div className="bg-[#E9FAF8] border border-[#00CDBE]/20 rounded-xl p-4 flex items-start justify-between" data-id="element-195">
          <div data-id="element-196">
            <h3 className="font-bold text-[#007E7A] mb-1" data-id="element-197">Connect POS</h3>
            <p className="text-sm text-[#007E7A]" data-id="element-198">
              Not connected. You can still earn Stash from pickups. Connect your
              POS to update automatically.
            </p>
          </div>
          <div className="flex items-center gap-4" data-id="element-199">
            <button onClick={() => setBusinessView('Settings')} className="bg-white text-[#007E7A] px-4 py-1.5 rounded-full text-sm font-bold shadow-sm hover:bg-gray-50" data-id="element-200">
              Connect
            </button>
            <button onClick={() => setShowPosBanner(false)} className="text-[#007E7A]/60 hover:text-[#007E7A]" data-id="element-201">
              <X className="w-5 h-5" data-id="element-202" />
            </button>
          </div>
        </div>}

      <div className="grid grid-cols-4 gap-4" data-id="element-203">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[rgba(37,41,53,0.12)] flex flex-col justify-between" data-id="element-204">
          <div className="flex items-center gap-2 text-[#60646F] mb-3" data-id="element-205">
            <CheckCircle2 className="w-4 h-4 text-[#00CDBE]" data-id="element-206" />
            <span className="text-sm font-bold uppercase tracking-wider" data-id="element-207">
              Match rate
            </span>
          </div>
          <div data-id="element-208">
            <div className="text-3xl font-extrabold text-[#252935] leading-none mb-1" data-id="element-209">
              96%
            </div>
            <div className="text-xs text-[#007E7A] font-medium" data-id="element-210">
              +2% this week
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[rgba(37,41,53,0.12)] flex flex-col justify-between" data-id="element-211">
          <div className="flex items-center gap-2 text-[#60646F] mb-3" data-id="element-212">
            <Package className="w-4 h-4 text-[#00CDBE]" data-id="element-213" />
            <span className="text-sm font-bold uppercase tracking-wider" data-id="element-214">
              Stashed items
            </span>
          </div>
          <div data-id="element-215">
            <div className="text-3xl font-extrabold text-[#252935] leading-none mb-1 flex items-baseline gap-1" data-id="element-216">
              {stashedCount}
              <span className="text-lg text-[#60646F] font-medium" data-id="element-217">
                / {products.length}
              </span>
            </div>
            <div className="text-xs text-[#60646F]" data-id="element-218">Active on menu</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[rgba(37,41,53,0.12)] flex flex-col justify-between" data-id="element-219">
          <div className="flex items-center gap-2 text-[#60646F] mb-3" data-id="element-220">
            <TrendingUp className="w-4 h-4 text-[#00CDBE]" data-id="element-221" />
            <span className="text-sm font-bold uppercase tracking-wider" data-id="element-222">
              Reserves
            </span>
          </div>
          <div data-id="element-223">
            <div className="text-3xl font-extrabold text-[#252935] leading-none mb-1" data-id="element-224">
              142
            </div>
            <div className="text-xs text-[#60646F]" data-id="element-225">This week</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[rgba(37,41,53,0.12)] flex flex-col justify-between" data-id="element-226">
          <div className="flex items-center gap-2 text-[#60646F] mb-3" data-id="element-227">
            <DollarSign className="w-4 h-4 text-[#00CDBE]" data-id="element-228" />
            <span className="text-sm font-bold uppercase tracking-wider" data-id="element-229">
              Est. Revenue
            </span>
          </div>
          <div data-id="element-230">
            <div className="text-3xl font-extrabold text-[#252935] leading-none mb-1" data-id="element-231">
              $4,180
            </div>
            <div className="text-xs text-[#007E7A] font-medium" data-id="element-232">
              Driven by Stash
            </div>
          </div>
        </div>
      </div>

      {/* Stash Seller Gamification & Map */}
      <div className="grid grid-cols-2 gap-4" data-id="element-233">
        {/* Gamified Progress Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-[rgba(37,41,53,0.12)] flex flex-col" data-id="element-234">
          <div className="flex items-center gap-2 mb-4" data-id="element-235">
            <ShieldCheck className={`w-6 h-6 ${isSeller ? 'text-[#00CDBE]' : 'text-[#60646F]'}`} data-id="element-236" />
            <h2 className="text-xl font-bold text-[#252935]" data-id="element-237">
              Stash Seller Status
            </h2>
          </div>

          <div className="flex-1 flex flex-col justify-center" data-id="element-238">
            <div className="flex justify-between items-end mb-2" data-id="element-239">
              <span className="text-sm font-bold text-[#60646F] uppercase tracking-wider" data-id="element-240">
                Progress
              </span>
              <span className="text-lg font-extrabold text-[#252935]" data-id="element-241">
                {currentStats?.stashedSkus}{' '}
                <span className="text-[#60646F] text-sm" data-id="element-242">
                  / {STASH_SELLER_THRESHOLD}
                </span>
              </span>
            </div>

            <div className="h-4 bg-[#F4F5F6] rounded-full overflow-hidden mb-4 border border-[rgba(37,41,53,0.12)]" data-id="element-243">
              <div className={`h-full transition-all duration-1000 ease-out ${isSeller ? 'bg-[#00CDBE]' : 'bg-[#252935]'}`} style={{
              width: `${progress * 100}%`
            }} data-id="element-244" />
            </div>

            {isSeller ? <div className="bg-[#E9FAF8] rounded-xl p-4 border border-[#00CDBE]/20" data-id="element-245">
                <p className="text-sm font-bold text-[#007E7A] mb-1" data-id="element-246">
                  You are a Stash Seller!
                </p>
                <p className="text-xs text-[#007E7A]/80" data-id="element-247">
                  Your products now feature the Stash Seller badge, boosting
                  your ranking and unlocking priority support.
                </p>
              </div> : <div className="bg-gray-50 rounded-xl p-4 border border-[rgba(37,41,53,0.12)]" data-id="element-248">
                <p className="text-sm font-bold text-[#252935] mb-1" data-id="element-249">
                  Get{' '}
                  {STASH_SELLER_THRESHOLD - (currentStats?.stashedSkus ?? 0)}{' '}
                  more to unlock!
                </p>
                <p className="text-xs text-[#60646F]" data-id="element-250">
                  Reach {STASH_SELLER_THRESHOLD} Stashed SKUs to earn the Stash
                  Seller badge, better organic rankings, and priority support.
                </p>
              </div>}
          </div>
        </div>

        {/* Competitor Map */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-[rgba(37,41,53,0.12)] flex flex-col" data-id="element-251">
          <div className="flex items-center justify-between mb-4" data-id="element-252">
            <h2 className="text-xl font-bold text-[#252935]" data-id="element-253">
              Local Competition
            </h2>
            <span className="text-xs font-medium text-[#60646F] bg-gray-100 px-2 py-1 rounded-md" data-id="element-254">
              2 mile radius
            </span>
          </div>

          <div className="relative flex-1 bg-[#F4F5F6] rounded-xl overflow-hidden border border-[rgba(37,41,53,0.12)] min-h-[200px]" data-id="element-255">
            {/* Subtle grid background */}
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#252935 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }} data-id="element-256"></div>

            {dispensaryStats.map(d => {
            const isMe = d.name === currentDispensaryName;
            return <div key={d.name} className="absolute group" style={{
              left: `${d.x}%`,
              top: `${d.y}%`,
              transform: 'translate(-50%, -50%)'
            }} data-id="element-257">
                  <div className={`relative flex items-center justify-center w-6 h-6 rounded-full shadow-md border-2 border-white transition-transform group-hover:scale-110 cursor-help z-10 ${isMe ? 'bg-[#252935] w-8 h-8 z-20' : d.isStashSeller ? 'bg-[#00CDBE]' : 'bg-[#A0A4B0]'}`} data-id="element-258">
                    {isMe ? <MapPin className="w-4 h-4 text-white" data-id="element-259" /> : d.isStashSeller ? <ShieldCheck className="w-3.5 h-3.5 text-white" data-id="element-260" /> : null}
                  </div>

                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30" data-id="element-261">
                    <div className="bg-[#252935] text-white text-xs rounded-lg py-1.5 px-2.5 shadow-lg flex flex-col items-center" data-id="element-262">
                      <span className="font-bold whitespace-nowrap" data-id="element-263">
                        {isMe ? 'You' : d.name}
                      </span>
                      <span className={`text-[10px] ${d.isStashSeller ? 'text-[#00CDBE]' : 'text-gray-300'}`} data-id="element-264">
                        {d.stashedSkus} Stashed SKU
                        {d.stashedSkus !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="w-2 h-2 bg-[#252935] rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2" data-id="element-265"></div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[rgba(37,41,53,0.12)] overflow-hidden" data-id="element-266">
        <div className="p-6 border-b border-[rgba(37,41,53,0.12)] bg-[#FFF8ED]" data-id="element-267">
          <div className="flex items-center gap-2" data-id="element-268">
            <AlertCircle className="w-5 h-5 text-[#C98A2E]" data-id="element-269" />
            <h2 className="text-lg font-bold text-[#252935]" data-id="element-270">
              Needs attention
            </h2>
          </div>
          <p className="text-sm text-[#60646F] mt-1" data-id="element-271">
            Items flagged as inaccurate by failed pickups.
          </p>
        </div>

        {flaggedProducts.length === 0 ? <div className="p-8 text-center text-[#60646F]" data-id="element-272">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-[#00CDBE]" data-id="element-273" />
            <p className="font-medium" data-id="element-274">All clear</p>
            <p className="text-sm" data-id="element-275">No items need attention right now.</p>
          </div> : <div className="divide-y divide-[rgba(37,41,53,0.12)]" data-id="element-276">
            {flaggedProducts.map(product => <div key={product.id} className="p-4 flex items-center justify-between hover:bg-gray-50" data-id="element-277">
                <div className="flex items-center gap-4" data-id="element-278">
                  <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover" data-id="element-279" />
                  <div data-id="element-280">
                    <h3 className="font-bold text-[#252935]" data-id="element-281">{product.name}</h3>
                    <p className="text-sm text-[#C98A2E] font-medium" data-id="element-282">
                      {product.failedPickups} failed pickups, shown in stock
                    </p>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-283">
                  <button onClick={() => toggleProductStock(product.id)} className="px-4 py-2 bg-white border border-[rgba(37,41,53,0.12)] rounded-full text-sm font-bold text-[#252935] hover:bg-gray-50" data-id="element-284">
                    Mark out of stock
                  </button>
                  <button onClick={() => setFixingProductId(product.id)} className="px-4 py-2 bg-[#00CDBE] text-white rounded-full text-sm font-bold hover:bg-[#00B8AA]" data-id="element-285">
                    Fix
                  </button>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
}